import type { UiOperation, UiOperationSource } from './operations'

export type UiOperationToolCallRead = {
  operation: UiOperation
  source: UiOperationSource
}

export type UiOperationToolCall = {
  type: unknown
  id?: string
  toolCallMessageId?: string
  toolPlan?: { name?: string; input?: unknown }
  data?: { tool?: { name?: string; id?: string; clientApplied?: boolean } }
  approval?: { status?: string }
}

// Written by the server once the call is settled. A live call has no approval field at all when the
// browser first sees it, so anything carrying one of these is history being replayed.
const RESOLVED_APPROVAL_STATUSES = ['approved', 'rejected', 'completed']

export function isResolvedToolCall(message: UiOperationToolCall): boolean {
  const status = message.approval?.status
  return typeof status === 'string' && RESOLVED_APPROVAL_STATUSES.includes(status)
}

export function isClientAppliedToolCall(message: UiOperationToolCall): boolean {
  return message.data?.tool?.clientApplied === true
}

export function readUiOperationToolCall(message: UiOperationToolCall, toolCallType: string): UiOperationToolCallRead | undefined {
  if (message.type !== toolCallType) return undefined
  if (!isClientAppliedToolCall(message)) return undefined
  if (isResolvedToolCall(message)) return undefined

  const name = message.toolPlan?.name ?? message.data?.tool?.name
  if (typeof name !== 'string' || !name) return undefined

  const id = message.toolCallMessageId ?? message.id
  if (!id) return undefined

  return { operation: toOperation(name, message.toolPlan?.input), source: { id, label: name } }
}

function toOperation(type: string, input: unknown): UiOperation {
  const parsed = typeof input === 'string' ? parseJson(input) : input

  return parsed && typeof parsed === 'object' && !Array.isArray(parsed) ? { ...(parsed as object), type } : { type }
}

function parseJson(value: string): unknown {
  try {
    return JSON.parse(value)
  } catch {
    return undefined
  }
}
